import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, GroupDot } from "@mixitone/components";
import TrashIcon from "@mixitone/icons/trash.svg";
import { Account, type Group } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { RoutedLink } from "components/common/Link";
import { useCallback } from "react";
import GroupListController from "./GroupListController";
import routeService from "services/RouteService";

interface Props {
  group: Group;
  groupRef(el: HTMLElement | null): void;
  onGroupClick(playerId: string): void;
}

const GroupRow: React.FC<Props> = ({ group }) => {
  const controller = GroupListController.use();
  const { club } = controller.state;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: group.uniqueId });

  const handleGroupDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    controller.actionDeleteGroup(group.id!);
  }, []);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr ref={setNodeRef} style={style} className="border-b border-slate-200" {...attributes}>
      <td className="w-[30px] cursor-grab py-2 pr-2" {...listeners}>
        <GroupDot group={group} size={24} />
      </td>
      <td>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            kind="link"
            onClick={(e) => {
              e.preventDefault();
              routeService.openModal("/clubs/:clubId/groups/:id/edit", {
                params: { clubId: club.id!, id: group.id! },
              });
            }}
          >
            {group.name || "New Group"}
          </Button>
          {Account.current?.hasFeature("group_levels") && group.graduation_enabled && (
            <span
              className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300"
              title="Graduation levels enabled"
            >
              Levels 1-2-3
            </span>
          )}
        </div>
      </td>
      <td className="text-sm">
        <RoutedLink
          kind="link"
          to="/clubs/:clubId/players"
          state={{ groupId: group.id }}
          params={{ clubId: club.id! }}
        >
          {group.players.count()}
        </RoutedLink>
      </td>
      <td className="text-right">
        <div className="flex items-center justify-center">
          {club.hasManagePermission() && (
            <button onClick={handleGroupDelete}>
              <TrashIcon className="h-[19px] w-[19px]" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default ApplicationView(GroupRow);
